import React, { useState } from 'react';
import { createSettlementConfig, getSettlementConfig, getSettlementConfigs } from 'services/apiSrv';
import styled from 'styled-components';
import { createSelectFormFieldConfig } from 'utils/formConfig';
import { api_srv } from 'services';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom'; 

const Container = styled.div`
  padding: 20px;
  font-family: 'Segoe UI', Arial, sans-serif;
  max-width: 900px;
  margin: 0 auto;
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 40px;
  color: #333;
  font-size: 1.8em;
`;

const ConfigCard = styled.div`
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  font-weight: 600;
  color: #444;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1em;
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1em;
  background: white;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 20px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  cursor: pointer;
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const ToggleSwitch = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  width: 50px;
  height: 26px;
  background: #ccc;
  border-radius: 26px;
  position: relative;
  cursor: pointer;
  transition: background 0.2s;

  &:checked {
    background: #4caf50;
  }

  &:after {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    background: white;
    border-radius: 50%;
    top: 2px;
    left: 2px;
    transition: left 0.2s;
  }

  &:checked:after {
    left: 26px;
  }
`;

const ChannelDetailsSection = styled.div`
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
`;

const ConditionalForm = styled.div`
  margin-top: 15px;
`;

const SaveButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 32px;
  font-size: 1.1em;
  cursor: pointer;
  align-self: flex-end;
  box-shadow: 0 2px 6px rgba(0, 123, 255, 0.3);

  &:hover {
    background: #0056b3;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 12px;
  overflow: hidden;
`;

const Th = styled.th`
  background: #f1f1f1;
  padding: 14px;
  text-align: left;
  font-weight: 600;
  color: #444;
`;

const Td = styled.td`
  padding: 14px;
  border-top: 1px solid #eee;
`;


const ActionLink = styled(Link)`
  background: none;
  border: none;
  color: #2563eb;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #1d4ed8;
  }
`;

function SettlementConfig(props) {
  const [selectedChannel, setSelectedChannel] = useState('mobile');
  const [mobileType, setMobileType] = useState('mobile');
  const [selectedConfig, setSelectedConfig] = useState({});

  const [formData, setFormData] = useState({
    selectedWallet: '',           // ← accountNumber of selected wallet
    thresholdAmount: '5000',      // ← actual threshold value
    frequency: 'EOD',
    thresholdEnabled: false,

    phoneNumber: '',
    paybillNumber: '',
    bankAccountNumber: '',
    bankName: '',
    branch: '',
  });

  const handleChannelChange = (e) => setSelectedChannel(e.target.value);
  const handleMobileTypeChange = (e) => setMobileType(e.target.value);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const { data: wallets, isLoading } = useQuery({
    queryKey: ['wallet'],
    queryFn: async () => {
      const resp = await (await api_srv).fetch_wallet_info();
      return resp.data;
    },
  });



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.selectedWallet) {
      alert('Please select a wallet');
      return;
    }

    const payload = {
      accountNumber: formData.selectedWallet,
      settlementConfigFrequency: formData.frequency,
      settlementConfigChannel: selectedChannel,
      PhoneNumber: '',
      bankAccountNumber: '',
    };

    // Add threshold conditionally
    if (formData.thresholdEnabled || formData.frequency === 'THRESHOLD') {
      const threshold = Number(formData.thresholdAmount);
      if (!isNaN(threshold) && threshold > 0) {
        payload.settlementConfigThreshold = threshold;
      }
    }

    // Channel-specific fields
    if (selectedChannel === 'mobile') {
      if (mobileType === 'mobile') {
        payload.PhoneNumber = formData.phoneNumber.trim();
        payload.settlementConfigChannel = 'mpesaNumber';
      } else {
        payload.PhoneNumber = formData.paybillNumber.trim();
        payload.settlementConfigChannel = 'mpesaPaybill';
      }
    } else if (selectedChannel === 'bank') {
      payload.bankAccountNumber = formData.bankAccountNumber.trim();
      payload.settlementConfigChannel = 'bank';
      // Optional: include these if your backend expects them
      // payload.bankName = formData.bankName.trim();
      // payload.branch = formData.branch.trim();
    }

    console.log('Submitting payload:', payload);

    try {
      const result = await createSettlementConfig(payload);
      console.log('Success:', result);
      alert('Configuration saved successfully!');
      
      // Optional: reset form or refetch list
      // setFormData({ ...initial state... });
    } catch (error) {
      console.error('Error:', error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        'Failed to save configuration. Please try again.';
      alert(msg);
    }
  };

    const { data: settlementConfigs = [], isConfigsLoading } = useQuery({
    queryKey: ['settlementConfigs'],
    queryFn: async () => {
      const resp = await getSettlementConfigs();
      return resp.data;
    },
  });

  const sortedConfigs = [...settlementConfigs]

  //single config 

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }); // → 12 Jan 2026
  };

  const getChannelLabel = (channel) => {
    if (channel === 'mpesaNumber') return 'M-Pesa';
    if (channel === 'bank') return 'Bank';
    return channel || 'Unknown';
  };

  return (
    <Container>
      <Title>Settlement Configuration</Title>

      <ConfigCard as="form" onSubmit={handleSubmit}>
        <FormGrid>
          <FormGroup>
            <Label>Wallet</Label>
            {isLoading ? (
              <div>Loading wallets...</div>
            ) : (
              <Select
                name="selectedWallet"
                value={formData.selectedWallet}
                onChange={handleInputChange}
                required
              >
                <option value="">— Select wallet —</option>
                {wallets?.map((wallet) => (
                  <option key={wallet.accountNumber} value={wallet.accountNumber}>
                  {wallet.accountName || '—'}
                  </option>
                ))}
              </Select>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Settlement Frequency</Label>
            <Select
              name="frequency"
              value={formData.frequency}
              onChange={handleInputChange}
            >
              <option value="EOD">Daily (EOD)</option>
              <option value="WEEKLY">Weekly</option>
              <option value="MONTHLY">Monthly</option>
              <option value="THRESHOLD">When threshold reached</option>
            </Select>
          </FormGroup>
        </FormGrid>

        {(formData.frequency === 'THRESHOLD' || formData.thresholdEnabled) && (
          <FormGroup>
            <Label>Threshold Amount (KES)</Label>
            <Input
              type="number"
              name="thresholdAmount"
              value={formData.thresholdAmount}
              onChange={handleInputChange}
              min="100"
              step="100"
              placeholder="e.g. 10000"
            />
          </FormGroup>
        )}

        <Toggle>
          Enable Threshold (override frequency)
          <ToggleSwitch
            type="checkbox"
            name="thresholdEnabled"
            checked={formData.thresholdEnabled}
            onChange={handleInputChange}
          />
        </Toggle>

        <Label>Settlement Channel</Label>
        <RadioGroup>
          <RadioLabel>
            <input
              type="radio"
              name="channel"
              value="mobile"
              checked={selectedChannel === 'mobile'}
              onChange={handleChannelChange}
            />
            Mobile Money
          </RadioLabel>
          <RadioLabel>
            <input
              type="radio"
              name="channel"
              value="bank"
              checked={selectedChannel === 'bank'}
              onChange={handleChannelChange}
            />
            Bank Account
          </RadioLabel>
        </RadioGroup>

        <Label>Destination Details</Label>

        {selectedChannel && (
          <ChannelDetailsSection>
            {selectedChannel === 'mobile' && (
              <>
                <strong>M-Pesa</strong>
                <RadioGroup>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="mobileType"
                      value="mobile"
                      checked={mobileType === 'mobile'}
                      onChange={handleMobileTypeChange}
                    />
                    Send to Phone Number
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="mobileType"
                      value="paybill"
                      checked={mobileType === 'paybill'}
                      onChange={handleMobileTypeChange}
                    />
                    Send to Paybill
                  </RadioLabel>
                </RadioGroup>

                {mobileType === 'mobile' && (
                  <FormGroup>
                    <Label>Phone Number (07...)</Label>
                    <Input
                      type="tel"
                      name="phoneNumber"
                      placeholder="e.g. 0712345678"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                )}

                {mobileType === 'paybill' && (
                  <FormGroup>
                    <Label>Paybill Number</Label>
                    <Input
                      type="text"
                      name="paybillNumber"
                      placeholder="e.g. 123456"
                      value={formData.paybillNumber}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                )}
              </>
            )}

            {selectedChannel === 'bank' && (
              <>
                <strong>Bank Transfer</strong>
                <FormGrid>
                  <FormGroup>
                    <Label>Account Number</Label>
                    <Input
                      type="text"
                      name="bankAccountNumber"
                      placeholder="e.g. 1234567890"
                      value={formData.bankAccountNumber}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Bank Name</Label>
                    <Input
                      type="text"
                      name="bankName"
                      placeholder="e.g. Equity Bank"
                      value={formData.bankName}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Branch</Label>
                    <Input
                      type="text"
                      name="branch"
                      placeholder="e.g. Westlands"
                      value={formData.branch}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </FormGrid>
              </>
            )}
          </ChannelDetailsSection>
        )}

        <SaveButton type="submit" disabled={isLoading}>
          Save Configuration
        </SaveButton>
      </ConfigCard>

      {/* You can later replace this with real data from an API */}
    <ConfigCard>
      <h3 style={{ marginTop: 0 }}>Existing Configurations</h3>

      {sortedConfigs.length === 0 ? (
        <p style={{ color: '#888', textAlign: 'center', padding: '20px 0' }}>
          No settlement configurations found.
        </p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Wallet</Th>
              <Th>Channel</Th>
              <Th>Frequency</Th>
              <Th>Created</Th>
              <Th>Action</Th>
            </tr>
          </thead>
          <tbody>
            {sortedConfigs.map((config, index) => {
              const { settlementConfigChannel, settlementConfigChannelDetails, settlementConfigFrequency, createdAt } = config;

              const phone = settlementConfigChannelDetails?.PhoneNumber || '—';
              const threshold = config.settlementConfigThreshold ?? 0;

              return (
                <tr key={config.id || config._id || index}>
                  <Td>
                    {phone}
                    <br />
                    Threshold: KES {threshold.toLocaleString()}
                  </Td>
                  <Td>{getChannelLabel(settlementConfigChannel)}</Td>
                  <Td style={{ textTransform: 'capitalize' }}>
                    {settlementConfigFrequency?.toLowerCase() || '—'}
                  </Td>
                  <Td>{formatDate(createdAt)}</Td>
                  <Td>
                      <ActionLink to={`/dashboard/settlements/config/${config.settlementConfigSystemRef}`}>
                        View
                      </ActionLink>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </ConfigCard>
    </Container>
  );
}
export default SettlementConfig;

